import { Navigate, useRoutes } from 'react-router-dom';
import { PrivateRoute } from '@/components/auth';

import ClinicAdminManagement from '@/pages/admin/clinics/ClinicAdminManagement';
import PatientManagementPage from '@/pages/admin/clinics/PatientManagement.page';
import ClinicManagementPage from '@/pages/admin/clinics/ClinicManagement.page';
import AdminDashboardPage from '@/pages/admin/clinics/AdminDashboard.page';
import PatientDetailPage from '@/pages/admin/clinics/PatientDetails.page';
import ForgotPasswordPage from '@/pages/auth/ForgotPassword.page';
import CompletedPage from '@/pages/Test/TestCompleted.page';
import TestLayoutPage from '@/pages/layouts/Test.layout';
import EntityDetails from '@/pages/EntityDetails.page';
import SimpleLayout from '@/pages/layouts/Main.layout';
import RegisterPage from '@/pages/auth/Register.page';
import LogoutPage from '@/pages/auth/Logout.page';
import ReviewTest from '@/pages/admin/ReviewTest';
import LoginPage from '@/pages/auth/Login.page';
import RoutingPage from '@/pages/Routing.page';
import TestPage from '@/pages/Test/Test.page';
import DebugPage from '@/pages/Debug.page';
import Page404 from '@/pages/Page404.js';
import Home from '@/pages/Home.page';

export default function Router() {
  const routes = useRoutes([
    // Auth routes -------------------------------------------------------------------------------
    {
      path: '/',
      element: <SimpleLayout />,
      children: [
        {
          element: (
            <RoutingPage
              routingMap={{
                admin: '/admin',
                doctor: '/patients',
                'clinic-admin': '/clinic-admin',
              }}
            />
          ),
          index: true,
        },
        {
          path: 'login',
          element: <LoginPage />,
        },
        {
          path: 'register',
          element: <RegisterPage />,
        },
        {
          path: 'forgot-password',
          element: <ForgotPasswordPage />,
        },
        {
          path: 'logout',
          element: <LogoutPage />,
        },
      ],
    },

    // Test routes --------------------------------------------------------------------------------
    {
      path: '/test/:id',
      element: <TestLayoutPage />,
      children: [
        { element: <Navigate to="./start" replace />, index: true },
        { path: 'start', element: <Home to="../0" /> },
        { path: 'complete', element: <CompletedPage /> },
        { path: ':step', element: <TestPage /> },
      ],
    },

    // Super Admin routes --------------------------------------------------------------------------------
    {
      path: '/admin',
      element: (
        <SimpleLayout>
          <PrivateRoute roles={['admin']} />
        </SimpleLayout>
      ),
      children: [
        { element: <AdminDashboardPage manage={['clinics', 'administrators', 'doctors', 'patients']} />, index: true },
        {
          path: 'clinics',
          children: [
            { element: <ClinicManagementPage />, index: true },
            { path: ':id', element: <EntityDetails entity="clinic" /> },
          ],
        },
        {
          path: 'doctors',
          children: [
            { element: <ClinicAdminManagement entity="doctor" />, index: true },
            { path: 'clinics/:clinicId/doctors/:id', element: <EntityDetails entity="doctor" /> },
          ],
        },
        {
          path: 'clinic-admins',
          children: [
            { element: <ClinicAdminManagement entity="clinic-admin" />, index: true },
            {
              path: 'clinics/:clinicId/clinic-admins/:id',
              element: <EntityDetails entity="clinic-admin" />,
            },
          ],
        },
        {
          path: 'patients',
          children: [
            { element: <PatientManagementPage />, index: true },

            {
              path: ':id',
              children: [
                { element: <PatientDetailPage />, index: true },
                { path: 'tests/:testId', element: <ReviewTest /> },
              ],
            },
            {
              path: ':clinicId/:id/edit',
              element: <EntityDetails entity="patient" />,
            },
          ],
        },
      ],
    },
    // Clinic-admin routes
    {
      path: '/clinic-admin',
      element: (
        <SimpleLayout>
          <PrivateRoute roles={['clinic-admin']} />
        </SimpleLayout>
      ),
      children: [
        {
          element: <AdminDashboardPage manage={['administrators', 'doctors', 'patients']} />,
          index: true,
        },
        {
          path: 'doctors',
          children: [
            { element: <ClinicAdminManagement entity="doctor" />, index: true },
            { path: 'clinics/:clinicId/doctors/:id', element: <EntityDetails entity="doctor" /> },
          ],
        },
        {
          path: 'clinic-admins',
          children: [
            {
              element: <ClinicAdminManagement entity="clinic-admin" />,
              index: true,
            },
            { path: 'clinics/:clinicId/clinic-admins/:id', element: <EntityDetails entity="clinic-admin" /> },
          ],
        },
        {
          path: 'patients',
          children: [
            { element: <PatientManagementPage />, index: true },

            { path: ':id', element: <PatientDetailPage />, index: true },

            {
              path: ':clinicId/:id/edit',
              element: <EntityDetails entity="patient" />,
            },
          ],
        },
      ],
    },

    //doctors
    {
      path: '/patients',
      element: (
        <SimpleLayout>
          <PrivateRoute roles={['doctor']} />
        </SimpleLayout>
      ),
      children: [
        { element: <PatientManagementPage />, index: true },
        {
          path: ':clinicId/:id/edit',
          element: <EntityDetails entity="patient" />,
        },
        {
          path: ':id',
          children: [
            { element: <PatientDetailPage />, index: true },
            { path: 'tests/:id', element: <ReviewTest /> },
          ],
        },
      ],
    },

    // Other routes --------------------------------------------------------------------------------
    {
      element: <SimpleLayout />,
      children: [
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '/debug',
      element: <SimpleLayout />,
      children: [{ element: <DebugPage />, index: true }],
    },
  ]);

  return routes;
}
